import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../graphql/hooks/useAuth';

export type NotificationType = 
  | 'request_approved' 
  | 'request_rejected' 
  | 'request_pending' 
  | 'system'
  | 'admin_action_required'
  | 'admin_status_update'
  | 'admin_system';

export type NotificationPriority = 'high' | 'medium' | 'low';

export interface Notification {
  id: string;
  type: NotificationType;
  priority?: NotificationPriority;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  relatedRequestId?: string;
  employeeName?: string;
  targetPage?: string; // Admin page to navigate to
}

interface NotificationsContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { userType } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Initialize with mock notifications
  useEffect(() => {
    if (userType) {
      // Admin notifications
      if (userType === 'admin') {
        const adminNotifications: Notification[] = [
          {
            id: 'admin-notif-1',
            type: 'admin_action_required',
            priority: 'high',
            title: 'Neuer Urlaubsantrag',
            message: 'Müller, Thomas - 15.01. bis 19.01.2026',
            timestamp: new Date(Date.now() - 30 * 60 * 1000), // 30 min ago
            read: false,
            employeeName: 'Müller, Thomas',
            relatedRequestId: 'REQ-101',
            targetPage: 'anträge',
          },
          {
            id: 'admin-notif-2',
            type: 'admin_action_required',
            priority: 'high',
            title: 'Überstundenabbau beantragt',
            message: 'Schmidt, Anna - 8,5 Stunden für 10.01.2026',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
            read: false,
            employeeName: 'Schmidt, Anna',
            relatedRequestId: 'REQ-102',
            targetPage: 'anträge',
          },
          {
            id: 'admin-notif-3',
            type: 'admin_action_required',
            priority: 'high',
            title: 'Krankmeldung eingegangen',
            message: 'Weber, Klaus - 13.01. bis 15.01.2026',
            timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
            read: false,
            employeeName: 'Weber, Klaus',
            relatedRequestId: 'REQ-103',
            targetPage: 'anträge',
          },
          {
            id: 'admin-notif-4',
            type: 'admin_status_update',
            priority: 'medium',
            title: 'Antrag genehmigt',
            message: 'Meyer, Lisa - Urlaubsantrag wurde von Admin 2 genehmigt',
            timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
            read: false,
            employeeName: 'Meyer, Lisa',
            relatedRequestId: 'REQ-104',
            targetPage: 'anträge',
          },
          {
            id: 'admin-notif-5',
            type: 'admin_status_update',
            priority: 'medium',
            title: 'Zeitkorrektur genehmigt',
            message: 'Fischer, Marc - Korrektur für 08.01.2026 wurde genehmigt',
            timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
            read: true,
            employeeName: 'Fischer, Marc',
            relatedRequestId: 'REQ-105',
            targetPage: 'anträge',
          },
          {
            id: 'admin-notif-6',
            type: 'admin_system',
            priority: 'low',
            title: 'Fehlende Zeiteinträge',
            message: '3 Mitarbeiter haben noch keine Einträge für diese Woche',
            timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
            read: true,
            targetPage: 'zeiterfassung',
          },
          {
            id: 'admin-notif-7',
            type: 'admin_system',
            priority: 'low',
            title: 'Monatsabschluss ausstehend',
            message: 'Dezember 2025 kann jetzt abgeschlossen werden',
            timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
            read: true,
            targetPage: 'month-closure',
          },
        ];
        setNotifications(adminNotifications);
      } else {
        // User notifications
        const userNotifications: Notification[] = [
          {
            id: 'notif-1',
            type: 'request_approved',
            title: 'Urlaubsantrag genehmigt',
            message: 'Ihr Urlaubsantrag vom 15.01. - 19.01.2026 wurde genehmigt',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
            read: false,
            relatedRequestId: 'REQ-001',
          },
          {
            id: 'notif-2',
            type: 'request_pending',
            title: 'Antrag in Bearbeitung',
            message: 'Ihr Krankmeldung wird derzeit von HR geprüft',
            timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
            read: false,
            relatedRequestId: 'REQ-002',
          },
          {
            id: 'notif-3',
            type: 'request_approved',
            title: 'Zeitkorrektur genehmigt',
            message: 'Ihre Zeitkorrektur für den 08.01.2026 wurde genehmigt',
            timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
            read: true,
            relatedRequestId: 'REQ-003',
          },
          {
            id: 'notif-4',
            type: 'request_rejected',
            title: 'Urlaubsantrag abgelehnt',
            message: 'Ihr Urlaubsantrag vom 22.01. - 26.01.2026 wurde abgelehnt',
            timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
            read: true,
            relatedRequestId: 'REQ-004',
          },
          {
            id: 'notif-5',
            type: 'system',
            title: 'System-Update',
            message: 'Die Anwendung wurde erfolgreich aktualisiert',
            timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
            read: true,
          },
          {
            id: 'notif-6',
            type: 'request_approved',
            title: 'Homeoffice-Antrag genehmigt',
            message: 'Ihr Homeoffice-Antrag für den 20.01.2026 wurde genehmigt',
            timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
            read: true,
            relatedRequestId: 'REQ-005',
          },
        ];
        setNotifications(userNotifications);
      }
    }
  }, [userType]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notif => ({ ...notif, read: true }))
    );
  };

  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `notif-${Date.now()}`,
      timestamp: new Date(),
      read: false,
    };

    setNotifications(prev => [newNotification, ...prev]);
  };

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        addNotification,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}